import {
  CANADA_STATES,
  CANADA_TERRITORIES,
  JURISDICTION_AREAS,
  UNITED_STATES_STATES,
  UNITED_STATES_TERRITORIES,
} from '@components/CaseSettingsBlocks/Jurisdiction/utils';
import { ColumnKey } from './types';

const emailRegex = /^[^@\s]+@[^@\s]+\.[^@\s]+$/u;

const phoneRegex = /^[\d\-()\s\\+]{7,}$/u;

const addressRegexes = [
  /^(?:(?:\d{1,6}\s+[A-Za-z0-9 .'-]+,?\s*)(?:[A-Za-z .'-]+,?\s*)(?:(?:A[LKZR]|C[AOT]|D[EC]|F[LM]|G[AU]|HI|I[ADLN]|K[SY]|LA|M[ADEHINOPST]|N[CDEHJMVY]|O[HKR]|P[AWR]|RI|S[CD]|T[NX]|UT|V[AIT]|W[AIVY])\s+\d{5}(?:-\d{4})?|(?:AB|BC|MB|NB|NL|NS|NT|NU|ON|PE|QC|SK|YT)\s+[A-Z]\d[A-Z]\s?\d[A-Z]\d)(?:,\s*(?:USA|United States|Canada))?|(?:\d{1,6}\s+(?:[A-Za-z][A-Za-z0-9.'-]*\s+){1,4}[A-Za-z][A-Za-z0-9.'-]*))$/iu,
];

export const JURISDICTION_MATCHING_VALUES = [
  ...UNITED_STATES_STATES.map((s) => s.label.toLowerCase()),
  ...UNITED_STATES_STATES.map((s) => s.value.toLowerCase()),
  ...UNITED_STATES_TERRITORIES.map((s) => s.label.toLowerCase()),
  ...UNITED_STATES_TERRITORIES.map((s) => s.value.toLowerCase()),
  ...CANADA_STATES.map((s) => s.label.toLowerCase()),
  ...CANADA_STATES.map((s) => s.value.toLowerCase()),
  ...CANADA_TERRITORIES.map((s) => s.label.toLowerCase()),
  ...CANADA_TERRITORIES.map((s) => s.value.toLowerCase()),
];

export const COUNTRIES_MATCHING_VALUES = [
  ...JURISDICTION_AREAS.map((area) => area.label),
  ...JURISDICTION_AREAS.map((area) => area.value),
  ...[
    'US',
    'us',
    'USA',
    'usa',
    'united states',
    'United States',
    'UNITED STATES',
    'United States of America',
    'united states of america',
    'ca',
    'CA',
    'CANADA',
    'canada',
    'Canada',
  ],
];

export const EMAIL_FIELDS = [
  ColumnKey.UnvalidatedEmail,
  ColumnKey.ValidatedEmail,
  ColumnKey.PaymentEmail,
];

export const REQUIRED_COLUMN_GROUPS = [
  {
    groupKey: 'email',
    keys: EMAIL_FIELDS,
    required: true,
    recommended: false,
    label: 'Email',
  },
];

export const EXPECTED_COLUMN_FIELDS = [
  {
    key: ColumnKey.AdminId,
    labels: ['adminID', 'admin_id', 'Admin ID'],
    required: true,
    recommended: false,
  },
  {
    key: ColumnKey.SubmissionDate,
    labels: [
      'submissionDate',
      'submission_date',
      'Submission Date',
      'Submitted At',
      'Date Submitted',
    ],
    valueRegex: [
      // 2025-06-25
      /^\d{4}-\d{2}-\d{2}$/u,
      // 06/25/2025
      /^\d{2}\/\d{2}\/\d{4}$/u,
      // 2025/06/25
      /^\d{4}\/\d{2}\/\d{2}$/u,
      // 25-Jun-2025
      /^\d{2}-[A-Za-z]{3}-\d{4}$/u,
      // 2025-06-25T12:34:56Z
      /\b\d{4}-(?:0[1-9]|1[0-2])-(?:0[1-9]|[12][0-9]|3[01])T(?:[01]\d|2[0-3]):[0-5]\d:[0-5]\d(?:\.\d+)?Z\b/u,
    ],
    required: true,
    recommended: false,
  },
  {
    key: ColumnKey.ClaimType,
    labels: ['claimType', 'claim_type', 'Claim Type'],
    required: true,
    recommended: false,
  },
  {
    key: ColumnKey.FirstName,
    labels: ['firstName', 'first_name', 'First Name'],
    required: true,
    recommended: false,
  },
  {
    key: ColumnKey.LastName,
    labels: ['lastName', 'last_name', 'Last Name'],
    required: true,
    recommended: false,
  },
  {
    key: ColumnKey.Address,
    labels: ['address', 'Address', 'address1', 'Address 1'],
    valueRegex: addressRegexes,
    required: true,
    recommended: false,
  },
  {
    key: ColumnKey.Address2,
    labels: ['address2', 'Address2', 'Address 2', 'address_line2'],
    valueRegex: addressRegexes,
    required: false,
    recommended: true,
  },
  {
    key: ColumnKey.City,
    labels: ['city', 'City'],
    required: true,
    recommended: false,
  },
  {
    key: ColumnKey.State,
    labels: ['states', 'States', 'state', 'State', 'province', 'Province'],
    allowedValues: JURISDICTION_MATCHING_VALUES,
    required: true,
    recommended: false,
  },
  {
    key: ColumnKey.Zip,
    labels: ['zip', 'Zip', 'zipcode', 'Zip Code'],
    valueRegex: [
      // US
      /^\d{5}(?:-\d{4})?$/u,
      // Canada
      /^[A-Za-z]\d[A-Za-z][ -]?\d[A-Za-z]\d$/u,
    ],
    required: true,
    recommended: false,
  },
  {
    key: ColumnKey.Country,
    labels: ['country', 'Country'],
    allowedValues: COUNTRIES_MATCHING_VALUES,
    required: true,
    recommended: false,
  },
  {
    key: ColumnKey.UnvalidatedEmail,
    labels: ['unvalidatedEmail', 'Unvalidated Email', 'unvalidated_email'],
    valueRegex: emailRegex,
  },
  {
    key: ColumnKey.ValidatedEmail,
    labels: ['validatedEmail', 'Validated Email', 'validated_email'],
    valueRegex: emailRegex,
  },
  {
    key: ColumnKey.PaymentEmail,
    labels: ['paymentEmail', 'Payment Email', 'payment_email'],
    valueRegex: emailRegex,
  },
  {
    key: ColumnKey.UnvalidatedPhone,
    labels: ['unvalidatedPhone', 'Unvalidated Phone', 'unvalidated_phone'],
    valueRegex: phoneRegex,
  },
  {
    key: ColumnKey.ValidatedPhone,
    labels: ['validatedPhone', 'Validated Phone', 'validated_phone'],
    valueRegex: phoneRegex,
  },
  {
    key: ColumnKey.PaymentPhone,
    labels: ['paymentPhone', 'Payment Phone', 'payment_phone'],
    valueRegex: phoneRegex,
  },
  {
    key: ColumnKey.Units,
    labels: ['units', 'Units'],
    valueRegex: /^\d+$/u,
    required: true,
    recommended: false,
  },
  {
    key: ColumnKey.IpAddress,
    labels: ['ipAddress', 'IP Address', 'ip_address'],
    valueRegex: /^(?:\d{1,3}\.){3}\d{1,3}$/u,
    required: true,
    recommended: false,
  },
  {
    key: ColumnKey.Referrer,
    labels: ['referrer', 'Referrer'],
    required: false,
    recommended: true,
  },
  {
    key: ColumnKey.MailedIn,
    labels: ['mailedIn', 'Mailed In', 'mailed_in'],
    required: false,
    recommended: true,
  },
  {
    key: ColumnKey.SubmitterType,
    labels: ['submitterType', 'Submitter Type', 'submitter_type'],
  },
  {
    key: ColumnKey.UniquePayoutAmount,
    labels: [
      'uniquePayoutAmount',
      'Unique Payout Amount',
      'unique_payout_amount',
    ],
    valueRegex: /^\d+(?:\.\d{2})?$/u,
  },
  {
    key: ColumnKey.PassesCaseSpecificValidation,
    labels: [
      'passesCaseSpecificValidation',
      'Passes Case Specific Validation',
      'passes_case_specific_validation',
    ],
  },
  {
    key: ColumnKey.ClassMemberId,
    labels: ['classMemberID', 'Class Member ID', 'class_member_id'],
  },
  {
    key: ColumnKey.AuthenticationPin,
    labels: ['authenticationPin', 'Authentication PIN', 'authentication_pin'],
    valueRegex: /^\d{4,8}$/u,
  },
  {
    key: ColumnKey.ArtifactPaidAmount,
    labels: [
      'artifactPaidAmount',
      'Artifact Paid Amount',
      'artifact_paid_amount',
    ],
    valueRegex: /^\d+(?:\.\d{2})?$/u,
  },
];

export const COLUMN_NUMBER_MIN = 13;
export const MATCH_THRESHOLD = 0.7;
export const MIN_LOADING_MS = 5000;
